import React, { useEffect, useState } from "react";
import { Box, Button, Chip, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { TransitionPromotionInput, TransitionStudent } from "../../../types";
import { ACADEMIC_STATUS, AcademicStatus } from "../../../../../modules/common/shared/types";

const FINAL_GRADE_NAME = "ten";

const getAcademicStatusOptions = (grade: string): AcademicStatus[] =>
  grade.trim().toLowerCase() === FINAL_GRADE_NAME
    ? [ACADEMIC_STATUS.Graduated, ACADEMIC_STATUS.Repeated, ACADEMIC_STATUS.Dropped, ACADEMIC_STATUS.Transferred]
    : [ACADEMIC_STATUS.Promoted, ACADEMIC_STATUS.Repeated, ACADEMIC_STATUS.Dropped, ACADEMIC_STATUS.Transferred];

const getDefaultAcademicStatus = (grade: string): AcademicStatus =>
  grade.trim().toLowerCase() === FINAL_GRADE_NAME ? ACADEMIC_STATUS.Graduated : ACADEMIC_STATUS.Promoted;

interface YearTransitionPromoteProps {
  grade: string;
  section: string;
  message: string;
  students: TransitionStudent[];
  acting: boolean;
  onPromote: (promotions: TransitionPromotionInput[]) => void;
}

// The promote step of the Year Transition wizard. Unlike the standalone
// Promotion page, the grade/section here is dictated by the backend (the
// wizard only ever shows whichever section the transition is currently on),
// so there's no grade/section picker — just the per-student status choice.
export const YearTransitionPromote: React.FC<YearTransitionPromoteProps> = ({ grade, section, message, students, acting, onPromote }) => {
  const [statusByAdmissionNo, setStatusByAdmissionNo] = useState<Record<string, AcademicStatus>>({});

  // Re-seed default statuses whenever a new grade/section arrives from the backend.
  useEffect(() => {
    const defaultStatus = getDefaultAcademicStatus(grade);
    setStatusByAdmissionNo(
      students.reduce((acc, s) => {
        acc[s.admissionNo] = defaultStatus;
        return acc;
      }, {} as Record<string, AcademicStatus>)
    );
  }, [grade, section]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = (e: SelectChangeEvent<AcademicStatus>, admissionNo: string) => {
    setStatusByAdmissionNo((prev) => ({ ...prev, [admissionNo]: e.target.value as AcademicStatus }));
  };

  const handlePromote = () => {
    const defaultStatus = getDefaultAcademicStatus(grade);
    onPromote(students.map((s) => ({ admissionNo: s.admissionNo, academicStatus: statusByAdmissionNo[s.admissionNo] ?? defaultStatus })));
  };

  const anyHasDue = students.some((s) => (s.totalDue ?? 0) > 0);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Chip label={`${grade} - ${section}`} color="primary" size="small" />
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
      <TableContainer className="mui-table-container-style">
        <Table
          size="small"
          className="mui-table-bordered"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { py: 0.5, px: 1, fontSize: 13 },
            "& .MuiTableCell-head": { fontWeight: 600 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Admission No</TableCell>
              {anyHasDue ? <TableCell sx={{ width: 90 }}>Total Due</TableCell> : ""}
              <TableCell sx={{ width: 150 }}>Guardian Phone No</TableCell>
              <TableCell sx={{ width: 150 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {students.map((s) => (
              <TableRow key={s.admissionNo}>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.admissionNo}</TableCell>
                {anyHasDue ? <TableCell>{s.totalDue}</TableCell> : ""}
                <TableCell>{s.guardianPhone}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={statusByAdmissionNo[s.admissionNo] ?? getDefaultAcademicStatus(grade)}
                    onChange={(e) => changeStatus(e, s.admissionNo)}
                    sx={{ width: 140, fontSize: 13, "& .MuiSelect-select": { py: 0.5 } }}
                  >
                    {getAcademicStatusOptions(grade).map((status) => (
                      <MenuItem value={status} key={status} sx={{ fontSize: 13 }}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button variant="contained" color="primary" size="small" disabled={acting} sx={{ mt: 1, mb: 1 }} onClick={handlePromote}>
        {acting ? "Promoting..." : `Promote ${grade} - ${section}`}
      </Button>
    </>
  );
};

export default YearTransitionPromote;
